import { useEffect, useRef, useCallback, useState } from "react";
import { useAuth } from "./use-auth";
import { buildWebSocketUrl, getAccessToken } from "@/api/client";

/** WebSocket event from the server. */
export interface WSEvent<T = unknown> {
  type: string;
  payload: T;
  sent_at: string;
}

/** Outbound event to send to the server. */
interface OutboundEvent {
  type: string;
  payload: unknown;
}

type EventHandler = (event: WSEvent) => void;

const HEARTBEAT_MS = 20_000;
const MAX_RECONNECT_MS = 30_000;
const BASE_RECONNECT_MS = 1_000;

/**
 * Persistent WebSocket hook with auto-reconnect, heartbeat, and event dispatch.
 * Returns helpers to send events and subscribe to event types.
 */
export function useWebSocket() {
  const { user } = useAuth();
  const wsRef = useRef<WebSocket | null>(null);
  const listenersRef = useRef<Map<string, Set<EventHandler>>>(new Map());
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const heartbeatTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const attemptRef = useRef(0);
  const [connected, setConnected] = useState(false);

  const connect = useCallback(() => {
    if (!user) return;

    const token = getAccessToken();
    if (!token) return;

    // Prefer backend origin when VITE_API_URL is set - SWA /api rewrites
    // do not support WebSocket upgrades (handshake returns 503).
    const url = buildWebSocketUrl("/ws");

    // Send the access token as a WebSocket subprotocol so connection errors
    // never expose credentials in the URL or browser console.
    const ws = new WebSocket(url, ["ehr-chat", `jwt.${token}`]);
    wsRef.current = ws;

    ws.onopen = () => {
      attemptRef.current = 0;
      setConnected(true);
      // Start heartbeat
      heartbeatTimer.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: "heartbeat", payload: {} }));
        }
      }, HEARTBEAT_MS);
    };

    ws.onmessage = (evt) => {
      try {
        const event: WSEvent = JSON.parse(evt.data);
        const handlers = listenersRef.current.get(event.type);
        if (handlers) {
          handlers.forEach((h) => h(event));
        }
        // Also fire wildcard listeners
        const all = listenersRef.current.get("*");
        if (all) {
          all.forEach((h) => h(event));
        }
      } catch {
        // Ignore malformed
      }
    };

    ws.onclose = () => {
      setConnected(false);
      if (heartbeatTimer.current) clearInterval(heartbeatTimer.current);
      // Exponential backoff reconnect
      const delay = Math.min(
        BASE_RECONNECT_MS * 2 ** attemptRef.current,
        MAX_RECONNECT_MS,
      );
      attemptRef.current += 1;
      reconnectTimer.current = setTimeout(connect, delay);
    };

    ws.onerror = () => {
      ws.close();
    };
  }, [user]);

  useEffect(() => {
    connect();
    return () => {
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      if (heartbeatTimer.current) clearInterval(heartbeatTimer.current);
      if (wsRef.current) {
        const socket = wsRef.current;
        socket.onclose = null; // Prevent reconnect on intentional close
        if (socket.readyState === WebSocket.CONNECTING) {
          socket.onopen = () => socket.close();
          socket.onerror = null;
        } else if (socket.readyState === WebSocket.OPEN) {
          socket.close();
        }
      }
    };
  }, [connect]);

  /** Send an event to the server via WebSocket. */
  const send = useCallback((event: OutboundEvent) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(event));
    }
  }, []);

  /** Subscribe to a specific event type. Returns an unsubscribe function. */
  const on = useCallback((eventType: string, handler: EventHandler) => {
    if (!listenersRef.current.has(eventType)) {
      listenersRef.current.set(eventType, new Set());
    }
    listenersRef.current.get(eventType)!.add(handler);
    return () => {
      listenersRef.current.get(eventType)?.delete(handler);
    };
  }, []);

  return { connected, send, on };
}
